package com.mgacreative.hotel.feature.reservation.ui.kbs

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.CheckCircle
import androidx.compose.material.icons.filled.Close
import androidx.compose.material.icons.filled.ContentCopy
import androidx.compose.material.icons.filled.Description
import androidx.compose.material.icons.filled.Print
import androidx.compose.material.icons.filled.Send
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.LocalClipboardManager
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import com.mgacreative.hotel.domain.model.KbsNotification

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun KbsFormDialog(
    initialKbs: KbsNotification,
    onDismiss: () -> Unit,
    onSendKbs: (KbsNotification) -> Unit = {}
) {
    var guestType by remember { mutableStateOf(initialKbs.guestType.ifBlank { "TC Vatandaşı" }) }
    var identityNo by remember { mutableStateOf(initialKbs.identityNo) }
    var firstName by remember { mutableStateOf(initialKbs.firstName) }
    var lastName by remember { mutableStateOf(initialKbs.lastName) }
    var birthDate by remember { mutableStateOf(initialKbs.birthDate.ifBlank { "01.01.1990" }) }
    var roomNumber by remember { mutableStateOf(initialKbs.roomNumber) }
    var checkInDateTime by remember { mutableStateOf(initialKbs.checkInDateTime.ifBlank { "26.07.2026 14:00" }) }
    var checkOutDateTime by remember { mutableStateOf(initialKbs.checkOutDateTime.ifBlank { "30.07.2026 11:00" }) }

    val clipboardManager = LocalClipboardManager.current
    var showSnackbar by remember { mutableStateOf(false) }
    var snackbarMessage by remember { mutableStateOf("") }
    var isSubmitted by remember { mutableStateOf(initialKbs.status == "BİLDİRİLDİ") }

    val kbsFormattedText = remember(
        guestType, identityNo, firstName, lastName, birthDate, roomNumber, checkInDateTime, checkOutDateTime
    ) {
        """
--------------------------------------------------
T.C. İÇİŞLERİ BAKANLIĞI
EMNİYET / JANDARMA KİMLİK BİLDİRİM SİSTEMİ (KBS)
GİRİŞ BİLDİRİM FORMU
--------------------------------------------------
Müşteri Tipi      : $guestType
Kimlik / Pasaport : ${identityNo.ifBlank { "GİRİLMEDİ" }}
Adı               : $firstName
Soyadı            : $lastName
Doğum Tarihi      : $birthDate
Oda Numarası      : Oda $roomNumber
Giriş Tarih / Saat: $checkInDateTime
Çıkış Tarih / Saat: $checkOutDateTime
Bildirim Durumu   : ${if (isSubmitted) "E-KBS SİSTEMİNE GÖNDERİLDİ" else "BİLDİRİM BEKLİYOR"}
--------------------------------------------------
        """.trimIndent()
    }

    val scrollState = rememberScrollState()

    Dialog(
        onDismissRequest = onDismiss,
        properties = DialogProperties(usePlatformDefaultWidth = false)
    ) {
        Surface(
            modifier = Modifier
                .fillMaxWidth(0.95f)
                .fillMaxHeight(0.9f),
            shape = RoundedCornerShape(16.dp),
            color = Color.White,
            shadowElevation = 8.dp
        ) {
            Column(modifier = Modifier.fillMaxSize()) {
                // Header Bar
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .background(Color(0xFF1E3A8A))
                        .padding(horizontal = 20.dp, vertical = 16.dp),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        Icon(Icons.Default.Description, contentDescription = null, tint = Color.White)
                        Spacer(Modifier.width(10.dp))
                        Column {
                            Text(
                                "KBS Kimlik Bildirim Formu",
                                fontWeight = FontWeight.Bold,
                                color = Color.White,
                                fontSize = 18.sp
                            )
                            Text(
                                "Emniyet & Jandarma Otomatik Bildirimi",
                                color = Color.White.copy(alpha = 0.8f),
                                fontSize = 12.sp
                            )
                        }
                    }
                    IconButton(onClick = onDismiss) {
                        Icon(Icons.Default.Close, contentDescription = "Kapat", tint = Color.White)
                    }
                }

                // Main Form Content
                Column(
                    modifier = Modifier
                        .weight(1f)
                        .verticalScroll(scrollState)
                        .padding(20.dp)
                ) {
                    // Status Badge
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .background(if (isSubmitted) Color(0xFFD1FAE5) else Color(0xFFFEF3C7), RoundedCornerShape(10.dp))
                            .padding(12.dp),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.SpaceBetween
                    ) {
                        Row(verticalAlignment = Alignment.CenterVertically) {
                            Icon(
                                imageVector = if (isSubmitted) Icons.Default.CheckCircle else Icons.Default.Description,
                                contentDescription = null,
                                tint = if (isSubmitted) Color(0xFF059669) else Color(0xFFD97706)
                            )
                            Spacer(Modifier.width(8.dp))
                            Text(
                                text = if (isSubmitted) "KBS Bildirimi Başarıyla Yapıldı" else "KBS Formu Doldurulmayı Bekliyor",
                                fontWeight = FontWeight.Bold,
                                color = if (isSubmitted) Color(0xFF065F46) else Color(0xFF92400E),
                                fontSize = 14.sp
                            )
                        }
                    }

                    Spacer(Modifier.height(16.dp))

                    // 1. Müşteri Tipi Seçimi
                    Text("1. Müşteri Tipi", fontWeight = FontWeight.Bold, fontSize = 14.sp, color = Color(0xFF1E293B))
                    Spacer(Modifier.height(8.dp))
                    Row(horizontalArrangement = Arrangement.spacedBy(10.dp)) {
                        FilterChip(
                            selected = guestType == "TC Vatandaşı",
                            onClick = { guestType = "TC Vatandaşı" },
                            label = { Text("TC Vatandaşı") },
                            modifier = Modifier.weight(1f)
                        )
                        FilterChip(
                            selected = guestType == "Yabancı",
                            onClick = { guestType = "Yabancı" },
                            label = { Text("Yabancı (Pasaport)") },
                            modifier = Modifier.weight(1f)
                        )
                    }

                    Spacer(Modifier.height(14.dp))

                    // 2. Kimlik & Kişisel Bilgiler
                    Text("2. Kimlik ve Kişisel Bilgiler", fontWeight = FontWeight.Bold, fontSize = 14.sp, color = Color(0xFF1E293B))
                    Spacer(Modifier.height(8.dp))

                    OutlinedTextField(
                        value = identityNo,
                        onValueChange = { identityNo = it },
                        label = { Text(if (guestType == "TC Vatandaşı") "TC Kimlik No" else "Pasaport No") },
                        modifier = Modifier.fillMaxWidth(),
                        shape = RoundedCornerShape(10.dp),
                        singleLine = true
                    )

                    Spacer(Modifier.height(10.dp))

                    Row(horizontalArrangement = Arrangement.spacedBy(10.dp)) {
                        OutlinedTextField(
                            value = firstName,
                            onValueChange = { firstName = it },
                            label = { Text("Ad") },
                            modifier = Modifier.weight(1f),
                            shape = RoundedCornerShape(10.dp),
                            singleLine = true
                        )
                        OutlinedTextField(
                            value = lastName,
                            onValueChange = { lastName = it },
                            label = { Text("Soyad") },
                            modifier = Modifier.weight(1f),
                            shape = RoundedCornerShape(10.dp),
                            singleLine = true
                        )
                    }

                    Spacer(Modifier.height(10.dp))

                    OutlinedTextField(
                        value = birthDate,
                        onValueChange = { birthDate = it },
                        label = { Text("Doğum Tarihi (GG.AA.YYYY)") },
                        modifier = Modifier.fillMaxWidth(),
                        shape = RoundedCornerShape(10.dp),
                        singleLine = true
                    )

                    Spacer(Modifier.height(14.dp))

                    // 3. Konaklama ve Oda Bilgileri
                    Text("3. Konaklama ve Oda Bilgileri", fontWeight = FontWeight.Bold, fontSize = 14.sp, color = Color(0xFF1E293B))
                    Spacer(Modifier.height(8.dp))

                    OutlinedTextField(
                        value = roomNumber,
                        onValueChange = { roomNumber = it },
                        label = { Text("Oda Numarası") },
                        modifier = Modifier.fillMaxWidth(),
                        shape = RoundedCornerShape(10.dp),
                        singleLine = true
                    )

                    Spacer(Modifier.height(10.dp))

                    Row(horizontalArrangement = Arrangement.spacedBy(10.dp)) {
                        OutlinedTextField(
                            value = checkInDateTime,
                            onValueChange = { checkInDateTime = it },
                            label = { Text("Giriş Tarihi ve Saati") },
                            modifier = Modifier.weight(1f),
                            shape = RoundedCornerShape(10.dp),
                            singleLine = true
                        )
                        OutlinedTextField(
                            value = checkOutDateTime,
                            onValueChange = { checkOutDateTime = it },
                            label = { Text("Çıkış Tarihi ve Saati") },
                            modifier = Modifier.weight(1f),
                            shape = RoundedCornerShape(10.dp),
                            singleLine = true
                        )
                    }

                    Spacer(Modifier.height(20.dp))

                    // Live Form Preview Card
                    Text("KBS Bildirim Formu Canlı Önizleme", fontWeight = FontWeight.Bold, fontSize = 14.sp, color = Color(0xFF1E293B))
                    Spacer(Modifier.height(8.dp))

                    Card(
                        modifier = Modifier
                            .fillMaxWidth()
                            .border(1.dp, Color(0xFFCBD5E1), RoundedCornerShape(10.dp)),
                        colors = CardDefaults.cardColors(containerColor = Color(0xFFF8FAFC)),
                        shape = RoundedCornerShape(10.dp)
                    ) {
                        Text(
                            text = kbsFormattedText,
                            modifier = Modifier.padding(14.dp),
                            fontSize = 12.sp,
                            color = Color(0xFF334155),
                            lineHeight = 18.sp
                        )
                    }
                }

                // Footer Buttons
                Surface(
                    modifier = Modifier.fillMaxWidth(),
                    color = Color.White,
                    shadowElevation = 8.dp
                ) {
                    Column(modifier = Modifier.padding(16.dp)) {
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.spacedBy(10.dp)
                        ) {
                            OutlinedButton(
                                onClick = {
                                    clipboardManager.setText(AnnotatedString(kbsFormattedText))
                                    snackbarMessage = "KBS bildirim metni panoya kopyalandı!"
                                    showSnackbar = true
                                },
                                modifier = Modifier.weight(1f).height(46.dp),
                                shape = RoundedCornerShape(10.dp)
                            ) {
                                Icon(Icons.Default.ContentCopy, contentDescription = null, modifier = Modifier.size(16.dp))
                                Spacer(Modifier.width(4.dp))
                                Text("Kopyala", fontSize = 12.sp)
                            }

                            OutlinedButton(
                                onClick = {
                                    clipboardManager.setText(AnnotatedString(kbsFormattedText))
                                    snackbarMessage = "KBS Bildirim çıktısı yazıcıya gönderildi!"
                                    showSnackbar = true
                                },
                                modifier = Modifier.weight(1f).height(46.dp),
                                shape = RoundedCornerShape(10.dp)
                            ) {
                                Icon(Icons.Default.Print, contentDescription = null, modifier = Modifier.size(16.dp))
                                Spacer(Modifier.width(4.dp))
                                Text("Yazdır", fontSize = 12.sp)
                            }
                        }

                        Spacer(Modifier.height(8.dp))

                        Button(
                            onClick = {
                                isSubmitted = true
                                val updatedKbs = initialKbs.copy(
                                    guestType = guestType,
                                    identityNo = identityNo,
                                    firstName = firstName,
                                    lastName = lastName,
                                    birthDate = birthDate,
                                    roomNumber = roomNumber,
                                    checkInDateTime = checkInDateTime,
                                    checkOutDateTime = checkOutDateTime,
                                    status = "BİLDİRİLDİ"
                                )
                                onSendKbs(updatedKbs)
                                snackbarMessage = "E-KBS Bildirimi Başarıyla Gönderildi!"
                                showSnackbar = true
                            },
                            modifier = Modifier.fillMaxWidth().height(48.dp),
                            shape = RoundedCornerShape(10.dp),
                            colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF1E3A8A))
                        ) {
                            Icon(Icons.Default.Send, contentDescription = null, modifier = Modifier.size(18.dp))
                            Spacer(Modifier.width(8.dp))
                            Text("E-KBS Sistemine Gönder", fontWeight = FontWeight.Bold)
                        }
                    }
                }
            }
        }
    }

    if (showSnackbar) {
        AlertDialog(
            onDismissRequest = { showSnackbar = false },
            title = { Text("KBS Bilgilendirme") },
            text = { Text(snackbarMessage) },
            confirmButton = {
                TextButton(onClick = { showSnackbar = false }) {
                    Text("Tamam")
                }
            }
        )
    }
}
